import { useState, useEffect } from 'react';
import {
  Container, Grid, Card, CardContent, Typography, Box, Alert, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, LinearProgress, Skeleton, IconButton,
} from '@mui/material';
import {
  BedDouble, CalendarCheck, LogIn, LogOut, DollarSign, Calendar,
  Wrench, CheckCircle, Plus, BarChart3, Settings, RefreshCw,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { hotelDashboardApi, HotelDashboardData, getDisplayAmount, getPaymentStatus } from '../../services/hotelApi';

const STATUS_COLORS: Record<string, string> = {
  pending: '#ff9800', 'checked-in': '#4caf50', 'checked-out': '#42a5f5', cancelled: '#f44336',
};

const Dashboard = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<HotelDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isFr = language === 'fr';
  const currency = user?.company?.signCurrency || '$';
  const hotelName = user?.company?.name || (isFr ? 'Mon Hôtel' : 'My Hotel');

  const t = isFr ? {
    title: 'Tableau de bord',
    subtitle: `Vue d'ensemble — ${hotelName}`,
    totalRooms: 'Chambres', available: 'Disponibles', occupied: 'Occupées', maintenance: 'Maintenance',
    pendingBookings: 'En attente', checkedIn: 'Check-In', todayIn: 'Arrivées auj.', todayOut: 'Départs auj.',
    roomStatus: 'Statut des Chambres', occupancyRate: 'Taux d\'occupation',
    revenueToday: 'Revenus Aujourd\'hui', revenueMonth: 'Revenus Ce Mois',
    total: 'Total', paid: 'Payé', remaining: 'Restant', bookings: 'réservations',
    revenueByDay: 'Revenus par jour', byPaymentMethod: 'Par méthode de paiement',
    topRooms: 'Top 5 Chambres', room: 'Chambre', nbBookings: 'Réservations', revenue: 'Revenus',
    recentBookings: 'Réservations Récentes', client: 'Client', status: 'Statut',
    amount: 'Montant', paidAmt: 'Payé', method: 'Méthode', viewAll: 'Voir tout',
    quickActions: 'Actions rapides',
    newBooking: 'Nouvelle réservation', addRoom: 'Ajouter chambre',
    viewRooms: 'Voir chambres', reports: 'Rapports', settings: 'Paramètres',
    cash: 'Espèces', mobile_money: 'Mobile Money', bank_transfer: 'Virement', card: 'Carte',
    errorLoading: 'Erreur lors du chargement', retry: 'Réessayer',
    pending: 'En attente', checkedInStatus: 'Arrivé', checkedOut: 'Parti', cancelled: 'Annulé',
  } : {
    title: 'Dashboard',
    subtitle: `Overview — ${hotelName}`,
    totalRooms: 'Rooms', available: 'Available', occupied: 'Occupied', maintenance: 'Maintenance',
    pendingBookings: 'Pending', checkedIn: 'Checked In', todayIn: 'Today Check-Ins', todayOut: 'Today Check-Outs',
    roomStatus: 'Room Status', occupancyRate: 'Occupancy Rate',
    revenueToday: 'Revenue Today', revenueMonth: 'Revenue This Month',
    total: 'Total', paid: 'Paid', remaining: 'Remaining', bookings: 'bookings',
    revenueByDay: 'Revenue by Day', byPaymentMethod: 'By Payment Method',
    topRooms: 'Top 5 Rooms', room: 'Room', nbBookings: 'Bookings', revenue: 'Revenue',
    recentBookings: 'Recent Bookings', client: 'Client', status: 'Status',
    amount: 'Amount', paidAmt: 'Paid', method: 'Method', viewAll: 'View all',
    quickActions: 'Quick Actions',
    newBooking: 'New Booking', addRoom: 'Add Room',
    viewRooms: 'View Rooms', reports: 'Reports', settings: 'Settings',
    cash: 'Cash', mobile_money: 'Mobile Money', bank_transfer: 'Bank Transfer', card: 'Card',
    errorLoading: 'Error loading data', retry: 'Retry',
    pending: 'Pending', checkedInStatus: 'Checked In', checkedOut: 'Checked Out', cancelled: 'Cancelled',
  };

  const basePath = `/${language}/hotel-dashboard`;

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await hotelDashboardApi.getStats();
      setData(result);
    } catch (err: any) {
      setError(err.message || t.errorLoading);
    } finally {
      setLoading(false);
    }
  };

  const fmtMethod = (m?: string) => {
    if (!m) return '-';
    const map: Record<string, string> = { cash: t.cash, mobile_money: t.mobile_money, bank_transfer: t.bank_transfer, card: t.card };
    return map[m] || m;
  };

  const fmtStatus = (s: string) => {
    const map: Record<string, string> = { pending: t.pending, 'checked-in': t.checkedInStatus, 'checked-out': t.checkedOut, cancelled: t.cancelled };
    return map[s] || s;
  };

  // Skeleton loader
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="text" width={300} height={40} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={200} height={24} sx={{ mb: 3 }} />
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Grid item xs={6} sm={3} md={1.5} key={i}>
              <Skeleton variant="rounded" height={110} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}><Skeleton variant="rounded" height={300} /></Grid>
          <Grid item xs={12} md={4}><Skeleton variant="rounded" height={300} /></Grid>
          <Grid item xs={12} md={4}><Skeleton variant="rounded" height={300} /></Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}><Skeleton variant="rounded" height={350} /></Grid>
          <Grid item xs={12} md={4}><Skeleton variant="rounded" height={350} /></Grid>
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="outlined" startIcon={<RefreshCw size={16} />} onClick={loadDashboard}>{t.retry}</Button>
      </Container>
    );
  }

  if (!data) return null;

  const roomsData = data.rooms || { total: 0, available: 0, occupied: 0, maintenance: 0 };
  const bookingsData = data.bookings || { total: 0, pending: 0, checkedIn: 0, checkedOut: 0, cancelled: 0, todayCheckIns: 0, todayCheckOuts: 0, bookingsToday: 0 };
  const revenueData = data.revenue || { today: { total: 0, paid: 0, bookings: 0 }, thisMonth: { total: 0, paid: 0, remaining: 0, bookings: 0 }, byDay: [], byPaymentMethod: {} };

  const occupancyPct = roomsData.total > 0 ? Math.round((roomsData.occupied / roomsData.total) * 100) : 0;

  const kpiCards = [
    { label: t.totalRooms, value: roomsData.total, icon: BedDouble, color: '#0D3C4C' },
    { label: t.available, value: roomsData.available, icon: CheckCircle, color: '#4caf50' },
    { label: t.occupied, value: roomsData.occupied, icon: BedDouble, color: '#f44336' },
    { label: t.maintenance, value: roomsData.maintenance, icon: Wrench, color: '#9e9e9e' },
    { label: t.pendingBookings, value: bookingsData.pending, icon: CalendarCheck, color: '#ff9800' },
    { label: t.checkedIn, value: bookingsData.checkedIn, icon: LogIn, color: '#4caf50' },
    { label: t.todayIn, value: bookingsData.todayCheckIns, icon: LogIn, color: '#2196f3' },
    { label: t.todayOut, value: bookingsData.todayCheckOuts, icon: LogOut, color: '#f44336' },
  ];

  const roomPieData = [
    { name: t.available, value: roomsData.available, color: '#4caf50' },
    { name: t.occupied, value: roomsData.occupied, color: '#f44336' },
    { name: t.maintenance, value: roomsData.maintenance, color: '#ff9800' },
  ];

  const pmEntries = Object.entries(revenueData.byPaymentMethod || {});
  const pmData = pmEntries.map(([k, v]) => ({ name: fmtMethod(k), value: v as number }));
  const PM_COLORS = ['#4caf50', '#2196f3', '#9c27b0', '#ff9800'];

  const byDayData = (revenueData.byDay || []).map(d => ({
    date: d.date ? d.date.slice(5) : '',
    [t.total]: d.total || 0,
    [t.paid]: d.paid || 0,
  }));

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#0D3C4C' }}>{t.title}</Typography>
            <Chip label={isFr ? 'Actif' : 'Active'} size="small" color="success" />
          </Box>
          <Typography variant="body1" color="text.secondary">{t.subtitle}</Typography>
        </Box>
        <IconButton onClick={loadDashboard}><RefreshCw size={20} /></IconButton>
      </Box>

      {/* 8 KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpiCards.map((c, i) => {
          const Icon = c.icon;
          return (
            <Grid item xs={6} sm={3} md={1.5} key={i}>
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center', py: 2, px: 1 }}>
                  <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'center' }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '10px', backgroundColor: `${c.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={20} color={c.color} />
                    </Box>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>{c.value}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{c.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Quick Actions */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>{t.quickActions}</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="small" variant="contained" startIcon={<Plus size={14} />} onClick={() => navigate(`${basePath}/${isFr ? 'nouvelle-reservation' : 'new-booking'}`)} sx={{ backgroundColor: '#0D3C4C', textTransform: 'none', '&:hover': { backgroundColor: '#164B61' } }}>{t.newBooking}</Button>
            <Button size="small" variant="outlined" startIcon={<BedDouble size={14} />} onClick={() => navigate(`${basePath}/${isFr ? 'chambres' : 'rooms'}`)} sx={{ textTransform: 'none' }}>{t.viewRooms}</Button>
            <Button size="small" variant="outlined" startIcon={<BarChart3 size={14} />} onClick={() => navigate(`${basePath}/${isFr ? 'rapports' : 'reports'}`)} sx={{ textTransform: 'none' }}>{t.reports}</Button>
            <Button size="small" variant="outlined" startIcon={<Settings size={14} />} onClick={() => navigate(`${basePath}/${isFr ? 'parametres' : 'settings'}`)} sx={{ textTransform: 'none' }}>{t.settings}</Button>
          </Box>
        </CardContent>
      </Card>

      {/* Room Status Pie + Occupation Bar + Revenue Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{t.roomStatus}</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={roomPieData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" paddingAngle={2}>
                    {roomPieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip formatter={(v: number) => v} />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value: string) => {
                      const item = roomPieData.find(d => d.name === value);
                      return `${value}: ${item?.value ?? 0}`;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              {/* Occupation bar */}
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">{t.occupancyRate}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>{occupancyPct}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={occupancyPct} sx={{ height: 8, borderRadius: 4, backgroundColor: '#e0e0e0', '& .MuiLinearProgress-bar': { borderRadius: 4, backgroundColor: occupancyPct > 80 ? '#f44336' : occupancyPct > 50 ? '#ff9800' : '#4caf50' } }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{t.revenueToday}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">{t.total}</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>{(revenueData.today.total || 0).toLocaleString()} {currency}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">{t.paid}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#4caf50' }}>{(revenueData.today.paid || 0).toLocaleString()} {currency}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">{revenueData.today.bookings || 0} {t.bookings}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{t.revenueMonth}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">{t.total}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>{(revenueData.thisMonth.total || 0).toLocaleString()} {currency}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">{t.paid}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#4caf50' }}>{(revenueData.thisMonth.paid || 0).toLocaleString()} {currency}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">{t.remaining}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#f44336' }}>{(revenueData.thisMonth.remaining || 0).toLocaleString()} {currency}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">{revenueData.thisMonth.bookings || 0} {t.bookings}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Revenue by Day */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{t.revenueByDay}</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={byDayData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => `${v.toLocaleString()} ${currency}`} />
                  <Legend />
                  <Bar dataKey={t.total} fill="#0D3C4C" radius={[4, 4, 0, 0]} />
                  <Bar dataKey={t.paid} fill="#4caf50" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top 5 Rooms */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{t.topRooms}</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t.room}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">{t.nbBookings}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">{t.revenue}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.topRooms || []).map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{r.roomNumber}</TableCell>
                    <TableCell align="center">{r.bookingsCount}</TableCell>
                    <TableCell align="right">{(r.totalRevenue || 0).toLocaleString()} {currency}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Recent Bookings */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{t.recentBookings}</Typography>
            <Button size="small" onClick={() => navigate(`${basePath}/${isFr ? 'reservations' : 'bookings'}`)} sx={{ textTransform: 'none' }}>{t.viewAll}</Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t.client}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t.room}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t.status}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">{t.amount}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">{t.paidAmt}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t.method}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.recentBookings || []).map((b: any) => {
                  const roomNum = typeof b.roomId === 'object' ? b.roomId?.roomNumber : '-';
                  const st = b.status?.replace(/_/g, '-') || 'pending';
                  return (
                    <TableRow key={b._id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{b.clientName}</Typography>
                        {b.nomFemme && <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>Ép. {b.nomFemme}</Typography>}
                      </TableCell>
                      <TableCell>{roomNum}</TableCell>
                      <TableCell>
                        <Chip label={fmtStatus(st)} size="small" sx={{ backgroundColor: `${STATUS_COLORS[st] || '#9e9e9e'}20`, color: STATUS_COLORS[st] || '#9e9e9e', fontWeight: 600, fontSize: '0.7rem' }} />
                      </TableCell>
                      <TableCell align="right">{(b.finalAmount || b.calculatedAmount || 0).toLocaleString()} {currency}</TableCell>
                      <TableCell align="right" sx={{ color: '#4caf50' }}>{(b.paidAmount || 0).toLocaleString()} {currency}</TableCell>
                      <TableCell>{fmtMethod(b.paymentMethod)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Dashboard;